/**
 * POS-I120: the merchandise value of a dining order as the server bills it, net of the line
 * (bargaining) discounts. One owner for the order detail page, the bill dialog and the takeaway
 * queue (they used to carry their own copies of this sum).
 *
 *  - cancelled lines and complimentary lines are worth nothing,
 *  - the price is the stored unit price (modifier prices are already inside it, the server bills
 *    `unitPrice x quantity`),
 *  - `discountAmount` is the priced money value the server stores (2 dp, capped at the line value).
 *
 * Tax is not part of this figure (the server adds exclusive tax at bill time).
 */

export interface DiningLineForTotal {
  quantity: number;
  unitPrice: number | string;
  kitchenStatus?: string;
  isComped?: boolean;
  discountAmount?: number | string | null;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const isBilledLine = (it: DiningLineForTotal) => it.kitchenStatus !== 'CANCELLED' && !it.isComped;

/** Line value before the line discount (0 for a cancelled or complimentary line). */
export function diningLineGross(it: DiningLineForTotal): number {
  return isBilledLine(it) ? round2(Number(it.unitPrice) * it.quantity) : 0;
}

/** Line discount in money, never above the line value. */
export function diningLineDiscount(it: DiningLineForTotal): number {
  if (!isBilledLine(it)) return 0;
  return Math.min(round2(Number(it.discountAmount ?? 0)), diningLineGross(it));
}

export function diningLineNet(it: DiningLineForTotal): number {
  return round2(diningLineGross(it) - diningLineDiscount(it));
}

export function diningItemsGross(items: DiningLineForTotal[]): number {
  return round2(items.reduce((s, it) => s + diningLineGross(it), 0));
}

export function diningItemsDiscount(items: DiningLineForTotal[]): number {
  return round2(items.reduce((s, it) => s + diningLineDiscount(it), 0));
}

/** Order value before tax, net of the line discounts. */
export function diningItemsNet(items: DiningLineForTotal[]): number {
  return round2(diningItemsGross(items) - diningItemsDiscount(items));
}
